import { QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { RouterProvider } from 'react-router';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { trpc, createTRPCClient, createQueryClient } from '@/lib/trpc';
import { router } from '@/routes';
import { StoreProvider, rootStore } from '@/stores';

/**
 * Root application component.
 *
 * Sets up all providers:
 * - tRPC + React Query for API communication
 * - MobX stores for state management
 * - React Router for navigation
 * - Error boundary for graceful error handling
 */
export function App(): React.ReactElement {
  // Create clients once and store in state to prevent recreation on re-renders
  const [queryClient] = useState(() => createQueryClient());
  const [trpcClient] = useState(() => createTRPCClient());
  const [initialized, setInitialized] = useState(false);

  // Initialize root store on mount
  useEffect(() => {
    void rootStore.initialize().then(() => {
      setInitialized(true);
    });
  }, []);

  // Show nothing until initialized (prevents flash)
  // The RootLayout will handle loading states
  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <StoreProvider>
            <RouterProvider router={router} />
          </StoreProvider>
        </QueryClientProvider>
      </trpc.Provider>
    </ErrorBoundary>
  );
}
